import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

const DEFAULT_SYMBOLS_FLAG_MAP = new Map<string, string>([
  // BRASIL
  ['TXJ_BCB', 'BR'],

  // USA
  ['DJIA-CFD', 'US'],
  ['DJIA', 'US'],
  ['NASDAQ-CFD', 'US'],
  ['NASDAQ', 'US'],
  ['SP500-CFD', 'US'],
  ['SP500', 'US'],
  ['USD', 'US'],
  ['IDOLCM', 'US'],
  ['DOLTR', 'US'],
  ['DOLPTAX', 'US'],
  ['TXJ_FED', 'US'],
  ['DOLPTAX_C', 'US'],
  ['DOLPTAX_P1', 'US'],
  ['DOLPTAX_P2', 'US'],
  ['DOLPTAX_P3', 'US'],
  ['DOLPTAX_P4', 'US'],
  ['DOLCM', 'US'],

  // BITCOIN
  ['BITCOIN-CFD', 'BTC'],
  ['BITCOIN', 'BTC'],

  // UK
  ['FTSE100-CFD', 'UK'],
  ['FTSE100', 'UK'],
  ['GBP', 'UK'],
  ['GBPCM', 'UK'],
  ['LIBOR', 'UK'],
  ['TXJ_BOE', 'UK'],

  // EUROPA
  ['EUR', 'UE'],
  ['EURCM', 'UE'],
  ['TXJ_ECB', 'UE'],

  // FR
  ['CAC40-CFD', 'FR'],
  ['CAC40', 'FR'],

  // DE
  ['DAX30-CFD', 'DE'],
  ['DAX30', 'DE'],

  // PT
  ['PSI20-CFD', 'PT'],
  ['PSI20', 'PT'],

  // JP
  ['NI225-CFD', 'JP'],
  ['NI225', 'JP'],
  ['JPYCM', 'JP'],
  ['JPY', 'JP'],
  ['TXJ_BOJ', 'JP'],

  // HK
  ['HSI-CFD', 'HK'],
  ['HSI', 'HK'],

  // IT
  ['MIB40-CFD', 'IT'],
  ['MIB40', 'IT'],

  // CH
  ['SSMI-CFD', 'CH'],
  ['SSMI', 'CH'],
  ['CHFCM', 'CH'],
  ['CHF', 'CH'],
  ['TXJ_SNB', 'CH'],

  // SE
  ['OMX30-CFD', 'SE'],
  ['OMX30', 'SE'],

  // AU
  ['AORD-CFD', 'AU'],
  ['AORD', 'AU'],
  ['AUDCM', 'AU'],
  ['AUD', 'AU'],
  ['TXJ_RBA', 'AU'],

  // NL
  ['AEX-CFD', 'NL'],
  ['AEX', 'NL'],

  // CA
  ['TXJ_BOC', 'CA'],
  ['CADCM', 'CA'],
  ['CAD', 'CA'],

  // RU
  ['TXJ_CBR', 'RU'],

  // CN
  ['TXJ_PBOC', 'CN'],

  // IN
  ['TXJ_RBI', 'IN'],

  // ES
  ['IBEX35', 'ES'],
  ['IBEX35-CFD', 'ES'],

  // AR
  ['ARSCM', 'AR'],
  ['ARS', 'AR'],

  // UY
  ['UYUCM', 'UY'],
  ['UYU', 'UY'],
]);

const DEFAULT_MARKET_FLAG_MAP = new Map<number, string>([
  // Brasil
  [1, 'BR'],
  [3, 'BR'],
  [6, 'BR'],
  [8, 'BR'],
  [45, 'BR'],
  [48, 'BR'],

  // EUA
  [10, 'US'],
  [11, 'US'],
  [12, 'US'],
  [18, 'US'],
  [21, 'US'],
  [22, 'US'],
  [23, 'US'],
  [24, 'US'],
  [52, 'US'],
  [53, 'US'],

  // Índices
  [4, 'IND'],
  [7, 'IND'],

  // Bitcoin
  [30, 'BTC'],
  [31, 'BTC'],

  // UK
  [13, 'UK'],
  [16, 'UK'],
  [55, 'UK'],

  // Canadá
  [54, 'CA'],
  [56, 'CA'],
  [60, 'CA'],

  // Portugal
  [58, 'PT'],

  // França
  [57, 'FR'],

  // Austrália
  [59, 'AU'],
]);


@Injectable({
  providedIn: 'root'
})
export class FlagsService {

  private symbolsFlagMap = new Map(DEFAULT_SYMBOLS_FLAG_MAP);
  private marketFlagMap = new Map(DEFAULT_MARKET_FLAG_MAP);

  constructor(private http: HttpClient) {
    this.loadMapOverwrite()
  }

  loadMapOverwrite(){
    this.http.get('/assets/config/flags.json').subscribe({
      next: (json: any) => {
        if (json.assets) {
          for (const key in json.assets) {
            this.symbolsFlagMap.set(key.toUpperCase(), json.assets[key]);
          }
        }
        if (json.markets) {
          for (const key in json.markets) {
            this.marketFlagMap.set(Number(key), json.markets[key]);
          }
        }
      },
      error: (err) => {
        console.warn('[FlagsService] Falha ao carregar overrides:', err.message);
      }
    });
  }

  getAssetFlag(asset: string, market: number): string {
    const normalizedAsset = asset?.toUpperCase();

    if (this.symbolsFlagMap.has(normalizedAsset)) {
      return this.symbolsFlagMap.get(normalizedAsset);
    }

    if (market === 44) {
      return normalizedAsset.startsWith('DOL') ? 'US' : 'BR';
    }

    return this.marketFlagMap.get(market) || null;
  }

  _getAssetFlag(asset, market): string {

    //  CODE - COUNTRY / TYPE
    // 'BR' BRASIL
    // 'US' USA
    // 'IND' Indices
    // 'BTC' Bitcoin
    // 'UK' Londres
    // 'UE' União Européia
    // 'FR' França
    // 'DE' Alemanha
    // 'PT' Portugal
    // 'JP' Japão
    // 'HK' Hong Kong
    // 'IT' Itália
    // 'CH' Suiça
    // 'SE' Suecia
    // 'AU' Austrália
    // 'NL' Holanda
    // 'CA' Canadá
    // 'RU' Russia
    // 'CN' China
    // 'IN' India
    // 'ES' Espanha
    // 'AR' Argentina
    // 'UY' Uruguai

    let assetFlag;

    switch (asset.toUpperCase()) {
      case 'TXJ_BCB':
        assetFlag = 'BR';
        break;
      case 'DJIA-CFD':
      case 'DJIA':
      case 'NASDAQ-CFD':
      case 'NASDAQ':
      case 'SP500-CFD':
      case 'SP500':
      case 'USD':
      case 'IDOLCM':
      case 'DOLTR':
      case 'DOLPTAX':
      case 'TXJ_FED':
      case 'DOLPTAX_C':
      case 'DOLPTAX_P1':
      case 'DOLPTAX_P2':
      case 'DOLPTAX_P3':
      case 'DOLPTAX_P4':
      case 'DOLCM':
        assetFlag = 'US';
        break;
      case 'BITCOIN-CFD':
      case 'BITCOIN':
        assetFlag = 'BTC';
        break;
      case 'FTSE100-CFD':
      case 'FTSE100':
      case 'GBP':
      case 'GBPCM':
      case 'LIBOR':
      case 'TXJ_BOE':
        assetFlag = 'UK';
        break;
      case 'EUR':
      case 'EURCM':
      case 'TXJ_ECB':
        assetFlag = 'UE';
        break;
      case 'CAC40-CFD':
      case 'CAC40':
        assetFlag = 'FR';
        break;
      case 'DAX30-CFD':
      case 'DAX30':
        assetFlag = 'DE';
        break;
      case 'PSI20-CFD':
      case 'PSI20':
        assetFlag = 'PT';
        break;
      case 'NI225-CFD':
      case 'NI225':
      case 'JPYCM':
      case 'JPY':
      case 'TXJ_BOJ':
        assetFlag = 'JP';
        break;
      case 'HSI-CFD':
      case 'HSI':
        assetFlag = 'HK';
        break;
      case 'MIB40-CFD':
      case 'MIB40':
        assetFlag = 'IT';
        break;
      case 'SSMI-CFD':
      case 'SSMI':
      case 'CHFCM':
      case 'CHF':
      case 'TXJ_SNB':
        assetFlag = 'CH';
        break;
      case 'OMX30-CFD':
      case 'OMX30':
        assetFlag = 'SE';
        break;
      case 'AORD-CFD':
      case 'AORD':
      case 'AUDCM':
      case 'AUD':
      case 'TXJ_RBA':
        assetFlag = 'AU';
        break;
      case 'AEX-CFD':
      case 'AEX':
        assetFlag = 'NL';
        break;
      case 'TXJ_BOC':
      case 'CADCM':
      case 'CAD':
        assetFlag = 'CA';
        break;
      case 'TXJ_CBR':
        assetFlag = 'RU';
        break;
      case 'TXJ_PBOC':
        assetFlag = 'CN';
        break;
      case 'TXJ_RBI':
        assetFlag = 'IN';
        break;
      case 'IBEX35':
      case 'IBEX35-CFD':
        assetFlag = 'ES';
        break;
      case 'ARSCM':
      case 'ARS':
        assetFlag = 'AR';
        break;
      case 'UYUCM':
      case 'UYU':
        assetFlag = 'UY';
        break;
      default:
        switch (market) {
          case 1:
          case 3:
          case 6:
          case 8:
          case 45:
          case 48:
            assetFlag = 'BR';
            break;
          case 10:
          case 11:
          case 12:
          case 18:
          case 21:
          case 22:
          case 23:
          case 24:
          case 52:
          case 53:
            assetFlag = 'US';
            break;
          case 4:
          case 7:
            assetFlag = 'IND';
            break;
          case 30:
          case 31:
            assetFlag = 'BTC';
            break;
          case 13:
          case 16:
            assetFlag = 'UK';
            break;
          case 54:
          case 56:
          case 60:
            assetFlag = 'CA';
            break;
          case 55:
            assetFlag = 'UK';
            break;
          case 58:
            assetFlag = 'PT';
            break;
          case 57:
            assetFlag = 'FR';
            break;
          case 59:
            assetFlag = 'AU';
            break;
          case 44: // datagro
            assetFlag = asset.toUpperCase().startsWith("DOL") ? 'US' : 'BR';
            break;
          default:
            break;
        }
        break;
    }
    return assetFlag;
  }
}
